import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Settings, CreditCard, Share2, LogOut, ShieldCheck, Award, X, ChevronRight, User as UserIcon, Mail, Check, FileText, History, ArrowDownLeft, ArrowUpRight, Truck, CheckCircle, AlertTriangle, Camera, Loader2 } from 'lucide-react';
import { client } from '../api/client';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../components/Toast/useToast';

// Modal component defined OUTSIDE Profile to prevent re-creation on state changes
const Modal = ({
  title,
  children,
  onClose
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
  >
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      className="bg-slate-900 w-full max-w-md rounded-3xl overflow-hidden border border-white/10"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-4 border-b border-white/10 flex justify-between items-center">
        <h3 className="font-bold text-white">{title}</h3>
        <button onClick={onClose} className="p-1 bg-white/10 rounded-full" aria-label="Close modal">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">
        {children}
      </div>
    </motion.div>
  </motion.div>
);

function Star(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

import { supabase } from '../lib/supabase';

// ... (existing imports, but keep client for auth)

export const Profile = () => {
  const [user, setUser] = useState<any>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const authContainerRef = useRef<HTMLDivElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Form State
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Reset Password State
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // Transaction History
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);

  // Escrow State
  const [trackingInput, setTrackingInput] = useState("");
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Profile stats (real data)
  const [profileStats, setProfileStats] = useState<{ cardCount: number; tradeCount: number; totalValue: number; loading: boolean }>({
    cardCount: 0, tradeCount: 0, totalValue: 0, loading: true
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleEscrowAction = async (action: 'mark_shipped' | 'confirm_receipt' | 'dispute', txId: number, payload: any = {}) => {
    setActionLoading(txId);
    try {
      const { error } = await supabase.functions.invoke('market-ops', {
        body: { action, transactionId: txId, ...payload }
      });

      if (error) throw error;

      // Refresh transactions
      // @ts-ignore
      fetchTransactions();
    } catch (e: any) {
      console.error(`Failed to ${action}`, e);
      alert(`Action failed: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">Pending</span>;
      case 'paid': return <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">Paid (Escrow)</span>;
      case 'shipped': return <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Shipped</span>;
      case 'completed': return <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Completed</span>;
      case 'disputed': return <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">Disputed</span>;
      default: return <span className="text-xs bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded-full">{status}</span>;
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      const session = await client.auth.getSession();
      const user = session?.data?.user;
      if (user) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (profile) {
            setUser({
              ...user,
              name: profile.display_name || user.name || '',
              image: profile.avatar_url || user.image || '',
            });
            setBio(profile.bio || '');
          } else {
            setUser(user);
          }
        } catch (e) {
          console.error("Failed to fetch profile", e);
          setUser(user);
        }
      }
    };
    checkSession();
  }, []);

  // Fetch real profile stats
  useEffect(() => {
    if (!user) return;
    const fetchProfileStats = async () => {
      try {
        const statsSession = await client.auth.getSession();
        const authUser = statsSession?.data?.user;
        if (!authUser) return;

        // Card count + total value
        const { data: cards } = await supabase
          .from('cards')
          .select('price')
          .eq('user_id', authUser.id);
        const cardCount = cards?.length || 0;
        const totalValue = (cards || []).reduce((sum: number, c: any) => sum + (Number(c.price) || 0), 0);

        // Trade count (completed transactions)
        const { count: tradeCount } = await supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .or(`buyer_id.eq.${authUser.id},seller_id.eq.${authUser.id}`);

        setProfileStats({ cardCount, tradeCount: tradeCount || 0, totalValue, loading: false });
      } catch (e) {
        console.error('Failed to fetch profile stats', e);
        setProfileStats(prev => ({ ...prev, loading: false }));
      }
    };
    fetchProfileStats();
  }, [user]);

  // Avatar upload handler
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const avatarSession = await client.auth.getSession();
      const authUser = avatarSession?.data?.user;
      if (!authUser) throw new Error('Not authenticated');

      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filePath = `${authUser.id}/avatar.${ext}`;

      // Upload to avatars bucket
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = urlData.publicUrl + '?t=' + Date.now(); // cache bust

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .upsert({ id: authUser.id, avatar_url: avatarUrl, updated_at: new Date().toISOString() });

      if (updateError) throw updateError;

      setUser((prev: any) => ({ ...prev, image: avatarUrl }));
      toast.success('Profile picture updated!');
    } catch (err: any) {
      console.error('Avatar upload failed', err);
      toast.error(err.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!user && authContainerRef.current) {
      // Clear any previously rendered auth UI (handles React StrictMode double-mount)
      authContainerRef.current.innerHTML = '';
      client.auth.renderAuthUI(authContainerRef.current, {
        redirectTo: '/profile',
        onLogin: async (u) => {
          // Fetch profile after login
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', u.id).single();
          if (profile) {
            setUser({ ...u, name: profile.display_name || u.name, image: profile.avatar_url || u.image });
            setBio(profile.bio || '');
          } else {
            setUser(u);
          }
        }
      });
      return () => { if (authContainerRef.current) authContainerRef.current.innerHTML = ''; };
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setDisplayName(user.name || '');
    }
  }, [user]);

  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleSaveProfile = async () => {
    if (!displayName.trim()) return;
    setIsSaving(true);
    try {
      const saveSession = await client.auth.getSession();
      const saveUser = saveSession?.data?.user;
      if (!saveUser) throw new Error("No user");

      const { error } = await supabase.from('profiles').upsert({
        id: saveUser.id,
        display_name: displayName.trim(),
        bio: bio.trim(),
        updated_at: new Date().toISOString()
      });

      if (error) throw error;

      setUser((prev: any) => ({
        ...prev,
        name: displayName.trim(),
      }));
      toast.success('Profile saved!');
      setBio(bio.trim());
      setActiveModal(null);
    } catch (e) {
      console.error("Failed to save profile", e);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTx(true);
    try {
      const txSession = await client.auth.getSession();
      const txUser = txSession?.data?.user;
      if (!txUser) return;

      const { data, error } = await supabase
        .from('transactions')
        .select('*, card:cards(name)')
        .or(`buyer_id.eq.${txUser.id},seller_id.eq.${txUser.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = data?.map((tx: any) => ({
        ...tx,
        direction: tx.buyer_id === user.id ? 'bought' : 'sold',
        createdAt: tx.created_at
      })) || [];

      setTransactions(formatted);
    } catch (e) {
      console.error("Failed to fetch transactions", e);
    } finally {
      setLoadingTx(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await client.auth.forgetPassword.emailOtp({ email: resetEmail });
      setResetSent(true);
    } catch (e: any) {
      alert("Error: " + e.message);
    }
  };

  if (!user) {
    return (
      <div className="p-4 pt-14 min-h-screen bg-obsidian flex flex-col items-center justify-center">
        <div ref={authContainerRef} className="w-full max-w-md" />
        <button
          onClick={() => { setActiveModal('reset-password'); setResetSent(false); setResetEmail(""); }}
          className="mt-4 text-slate-400 text-sm hover:text-white transition-colors"
        >
          Forgot Password?
        </button>

        <AnimatePresence>
          {activeModal === 'reset-password' && (
            <Modal title="Reset Password" onClose={handleCloseModal}>
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-white mb-2">Check your email!</p>
                  <p className="text-slate-400 text-sm">We've sent a password reset link to {resetEmail}</p>
                  <button onClick={handleCloseModal} className="mt-4 w-full bg-white/10 py-3 rounded-xl font-bold">Close</button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label htmlFor="reset-email" className="text-xs text-slate-400 block mb-2">Email Address</label>
                    <div className="flex items-center bg-black/30 rounded-xl p-3 border border-white/10">
                      <Mail className="w-5 h-5 text-slate-500 mr-3" />
                      <input
                        id="reset-email"
                        type="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="bg-transparent w-full text-white focus:outline-none"
                      />
                    </div>
                  </div>
                  <button type="submit" className="w-full bg-electric-600 py-3 rounded-xl font-bold">Send Reset Link</button>
                </form>
              )}
            </Modal>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 space-y-8 pb-32 min-h-screen bg-obsidian">
      <header className="text-center relative">
        <div className="w-32 h-32 mx-auto mb-4 relative group">
          <div className="absolute inset-0 bg-electric-500 rounded-full blur-2xl opacity-20 animate-pulse" />
          {user.image ? (
            <img
              src={user.image}
              alt={`${user.name || 'User'} profile avatar`}
              className="w-full h-full rounded-full object-cover border-4 border-obsidian relative z-10"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full rounded-full bg-copper-500/20 border-4 border-obsidian relative z-10 flex items-center justify-center">
              <span className="text-4xl font-display font-bold text-copper-400">
                {(user.name || user.email || 'C')[0].toUpperCase()}
              </span>
            </div>
          )}

          {/* Avatar upload overlay */}
          <button
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="absolute inset-0 rounded-full z-20 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
            aria-label="Change profile picture"
          >
            {uploadingAvatar ? (
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            ) : (
              <div className="flex flex-col items-center">
                <Camera className="w-6 h-6 text-white" />
                <span className="text-white text-xs mt-1 font-medium">Change</span>
              </div>
            )}
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarUpload}
            className="hidden"
            title="Upload profile picture"
          />

          <div className="absolute bottom-0 right-0 bg-blue-500 text-white p-1.5 rounded-full border-4 border-obsidian z-20">
            <ShieldCheck className="w-4 h-4" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-white font-display">{user.name || "Collector"}</h1>
        <p className="text-slate-400 text-sm">{user.email}</p>
        {bio && (
          <p className="text-slate-300 text-sm mt-2 max-w-xs mx-auto italic">"{bio}"</p>
        )}

        <div className="flex justify-center space-x-4 mt-6">
          <div className="glass px-6 py-3 rounded-2xl text-center min-w-[100px]">
            <div className="font-bold text-xl text-white">
              {profileStats.loading ? <span className="inline-block w-8 h-6 bg-white/10 rounded animate-pulse" /> : profileStats.cardCount}
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Cards</div>
          </div>
          <div className="glass px-6 py-3 rounded-2xl text-center min-w-[100px]">
            <div className="font-bold text-xl text-white">
              {profileStats.loading ? <span className="inline-block w-8 h-6 bg-white/10 rounded animate-pulse" /> : profileStats.tradeCount}
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Trades</div>
          </div>
          <div className="glass px-6 py-3 rounded-2xl text-center min-w-[100px]">
            <div className="font-bold text-xl text-white">
              {profileStats.loading ? <span className="inline-block w-12 h-6 bg-white/10 rounded animate-pulse" /> : `$${profileStats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Value</div>
          </div>
        </div>
      </header>

      <div className="space-y-3">
        <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider ml-1">Account</h3>

        <button onClick={() => setActiveModal('settings')} className="w-full glass p-4 rounded-2xl flex items-center justify-between group hover:bg-white/5 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-white/5 rounded-xl group-hover:bg-electric-500/20 group-hover:text-electric-400 transition-colors">
              <Settings className="w-5 h-5 text-slate-400 group-hover:text-electric-400" />
            </div>
            <span className="font-medium text-white">Settings</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </button>

        <button onClick={() => setActiveModal('payment')} className="w-full glass p-4 rounded-2xl flex items-center justify-between group hover:bg-white/5 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-white/5 rounded-xl group-hover:bg-electric-500/20 group-hover:text-electric-400 transition-colors">
              <CreditCard className="w-5 h-5 text-slate-400 group-hover:text-electric-400" />
            </div>
            <span className="font-medium text-white">Payment Methods</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </button>

        <button onClick={() => setActiveModal('share')} className="w-full glass p-4 rounded-2xl flex items-center justify-between group hover:bg-white/5 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-white/5 rounded-xl group-hover:bg-electric-500/20 group-hover:text-electric-400 transition-colors">
              <Share2 className="w-5 h-5 text-slate-400 group-hover:text-electric-400" />
            </div>
            <span className="font-medium text-white">Share Profile</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </button>

        <button onClick={() => { setActiveModal('transactions'); fetchTransactions(); }} className="w-full glass p-4 rounded-2xl flex items-center justify-between group hover:bg-white/5 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-white/5 rounded-xl group-hover:bg-electric-500/20 group-hover:text-electric-400 transition-colors">
              <History className="w-5 h-5 text-slate-400 group-hover:text-electric-400" />
            </div>
            <span className="font-medium text-white">Transaction History</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </button>

        <button
          onClick={() => {
            client.auth.signOut();
            setUser(null);
          }}
          className="w-full glass p-4 rounded-2xl flex items-center justify-between group hover:bg-red-500/10 transition-colors mt-6 border-red-500/20"
        >
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-red-500/10 rounded-xl">
              <LogOut className="w-5 h-5 text-red-500" />
            </div>
            <span className="font-medium text-red-500">Log Out</span>
          </div>
        </button>
      </div>

      <AnimatePresence>
        {activeModal === 'settings' && (
          <Modal title="Settings" onClose={handleCloseModal}>
            <div className="space-y-4">
              <div>
                <label htmlFor="display-name" className="text-xs text-slate-400 block mb-2">Display Name</label>
                <div className="flex items-center bg-black/30 rounded-xl p-3 border border-white/10">
                  <UserIcon className="w-5 h-5 text-slate-500 mr-3" />
                  <input
                    id="display-name"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    autoFocus
                    className="bg-transparent w-full text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="bio" className="text-xs text-slate-400 block mb-2">Bio / Personal Info</label>
                <div className="flex items-start bg-black/30 rounded-xl p-3 border border-white/10">
                  <FileText className="w-5 h-5 text-slate-500 mr-3 mt-1" />
                  <textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about your collection..."
                    className="bg-transparent w-full text-white focus:outline-none min-h-[80px] resize-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email-display" className="text-xs text-slate-400 block mb-2">Email</label>
                <div className="flex items-center bg-black/30 rounded-xl p-3 border border-white/10 opacity-50">
                  <Mail className="w-5 h-5 text-slate-500 mr-3" />
                  <input id="email-display" type="text" defaultValue={user.email} disabled className="bg-transparent w-full text-white focus:outline-none" />
                </div>
              </div>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving || !displayName.trim()}
                className="w-full bg-electric-600 py-3 rounded-xl font-bold mt-4 disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </Modal>
        )}

        {activeModal === 'payment' && (
          <Modal title="Payment Methods" onClose={handleCloseModal}>
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-400 mb-6">No payment methods added yet.</p>
              <button className="w-full bg-white/10 py-3 rounded-xl font-bold border border-white/10 hover:bg-white/20 transition-colors">
                + Add Card
              </button>
            </div>
          </Modal>
        )}

        {activeModal === 'share' && (
          <Modal title="Share Profile" onClose={handleCloseModal}>
            <div className="bg-white p-4 rounded-xl mb-4">
              <div className="w-full aspect-square bg-black flex items-center justify-center">
                <p className="text-white font-mono">QR CODE</p>
              </div>
            </div>
            <button className="w-full bg-electric-600 py-3 rounded-xl font-bold">Copy Link</button>
          </Modal>
        )}

        {activeModal === 'transactions' && (
          <Modal title="Transaction History" onClose={handleCloseModal}>
            {loadingTx ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-400">No transactions yet.</p>
                <p className="text-slate-500 text-xs mt-1">Buy or sell cards to see your history here.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {transactions.map((tx: any) => (
                  <div key={tx.id} className="p-3 bg-black/30 rounded-xl border border-white/5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tx.direction === 'bought' ? 'bg-blue-500/15' : 'bg-green-500/15'
                        }`}>
                        {tx.direction === 'bought' ? (
                          <ArrowDownLeft className="w-5 h-5 text-blue-400" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-green-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {tx.card?.name || 'Unknown Card'}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-slate-400 text-xs">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                          {getStatusBadge(tx.status || 'completed')}
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${tx.direction === 'bought' ? 'text-blue-400' : 'text-green-400'
                        }`}>
                        {tx.direction === 'bought' ? '-' : '+'}${typeof tx.amount === 'number' ? tx.amount.toFixed(2) : tx.amount}
                      </span>
                    </div>

                    {/* Escrow Actions */}
                    {tx.status !== 'completed' && tx.status !== 'refunded' && (
                      <div className="pt-2 border-t border-white/5 flex gap-2">
                        {/* Seller Actions */}
                        {tx.direction === 'sold' && tx.status === 'paid' && (
                          <div className="flex-1 flex gap-2">
                            <input
                              type="text"
                              placeholder="Tracking"
                              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
                              value={trackingInput}
                              onChange={(e) => setTrackingInput(e.target.value)}
                            />
                            <button
                              onClick={() => handleEscrowAction('mark_shipped', tx.id, { trackingNumber: trackingInput })}
                              disabled={!trackingInput || actionLoading === tx.id}
                              className="bg-purple-500/20 text-purple-400 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-purple-500/30 disabled:opacity-50"
                            >
                              {actionLoading === tx.id ? '...' : 'Mark Shipped'}
                            </button>
                          </div>
                        )}

                        {/* Buyer Actions */}
                        {tx.direction === 'bought' && (
                          <div className="flex-1 flex justify-end gap-2">
                            {tx.status === 'shipped' && (
                              <button
                                onClick={() => handleEscrowAction('confirm_receipt', tx.id)}
                                disabled={actionLoading === tx.id}
                                className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-500/30 flex items-center gap-1"
                              >
                                <CheckCircle size={12} />
                                Confirm
                              </button>
                            )}
                            {(tx.status === 'paid' || tx.status === 'shipped') && (
                              <button
                                onClick={() => handleEscrowAction('dispute', tx.id, { reason: 'Not received' })}
                                disabled={actionLoading === tx.id}
                                className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-500/30 flex items-center gap-1"
                              >
                                <AlertTriangle size={12} />
                                Dispute
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {tx.tracking_number && (
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Truck size={12} />
                        Tracking: <span className="text-slate-300 font-mono">{tx.tracking_number}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};
